// client/src/pages/Admin/ResourceManager/utils/resourceSearch.ts
import { Resource } from '../../../../shared/models/Resource';
import { getCategoryDisplay, getParentCategoryDisplay } from './resourceHelpers';

export const highlightMatch = (text: string, query: string): string => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
};

export const resourceMatchesSearch = (
    resource: Resource,
    query: string
): { matches: boolean; matchFields: string[] } => {
    if (!query) return { matches: true, matchFields: [] };

    const search = query.toLowerCase().trim();
    if (!search) return { matches: true, matchFields: [] };

    const matchFields: string[] = [];

    if ((resource.title ?? '').toLowerCase().includes(search)) {
        matchFields.push('title');
    }

    if ((resource.description ?? '').toLowerCase().includes(search)) {
        matchFields.push('description');
    }

    if ((resource.tags ?? []).some((tag: string) => tag.toLowerCase().includes(search))) {
        matchFields.push('tags');
    }

    if ((resource.authors ?? []).some((author: { name?: string }) => 
        (author.name ?? '').toLowerCase().includes(search)
    )) {
        matchFields.push('authors');
    }

    const categoryLabel = getCategoryDisplay(resource.category);
    if (categoryLabel.toLowerCase().includes(search)) {
        matchFields.push('category');
    }

    const parentLabel = getParentCategoryDisplay(resource.category);
    if (parentLabel.toLowerCase().includes(search)) {
        matchFields.push('parentCategory');
    }

    const resourceWithFiles = resource as Resource & { files?: Array<{ name?: string }> };
    if (resourceWithFiles.files && resourceWithFiles.files.length > 0) {
        const hasFileNameMatch = resourceWithFiles.files.some((file: { name?: string }) => 
            (file.name ?? '').toLowerCase().includes(search)
        );
        if (hasFileNameMatch) {
            matchFields.push('files');
        }
    }

    return {
        matches: matchFields.length > 0,
        matchFields
    };
};

export const generateSearchSuggestions = (
    resources: Resource[],
    searchTerm: string
): string[] => {
    if (!searchTerm || searchTerm.length < 2) {
        return [];
    }

    const search = searchTerm.toLowerCase().trim();
    const suggestions = new Set<string>();

    resources.forEach(resource => {
        if ((resource.title ?? '').toLowerCase().includes(search)) {
            suggestions.add(resource.title || '');
        }

        const category = getCategoryDisplay(resource.category);
        if (category.toLowerCase().includes(search)) {
            suggestions.add(`Category: ${category}`);
        }

        (resource.tags ?? []).forEach((tag: string) => {
            if (tag.toLowerCase().includes(search)) {
                suggestions.add(`Tag: ${tag}`);
            }
        });

        (resource.authors ?? []).forEach((author: { name?: string }) => {
            if ((author.name ?? '').toLowerCase().includes(search)) {
                suggestions.add(`Author: ${author.name}`);
            }
        });
    });

    return Array.from(suggestions).slice(0, 8);
};